/**
 * Sammelanzeiger der KI-Zugaenge fuer die Kopfleiste (ProjectTabs).
 */
import { KI_ROT_AB } from './kiGrenzen';
// --- Account Usage Types ---
export interface UsageAccount {
  accountId: string;
  accountName: string;
  status: 'safe' | 'warning' | 'critical' | 'stale';
  provider?: 'claude' | 'codex';
  quotaWindows?: { usedPercent: number; windowDurationMins: number; resetsAt: number }[];
  quotaError?: string | null;
  scrapedTimestamp?: string | null;
  /** Live von der CLI gemeldet (bridge.ts stats-v2) — schlaegt die Prozente des Ablesers. */
  cliSperre?: { until: number; window: string } | null;
  modellSperren?: { modell: string; until: number; text: string }[];
  scraped: {
    currentSession: { percent: number | null; resetIn: string };
    weeklyAllModels: { percent: number | null; resetDate: string };
    weeklySonnet: { percent: number | null; resetDate: string };
    extraUsage: { percent: number; spent: string; limit: string; balance: string };
  } | null;
}

export interface AccountHealth {
  status: 'ok' | 'drift' | 'unknown';
  expected_org_id?: string;
  token_org_id?: string;
  cookie_org_id?: string;
}

/** Ab diesem genutzten Anteil gilt ein Zugang als knapp (rot) — gemeinsame Grenze 95 %, lib/kiGrenzen.ts (Rafael 03.10.2026 16:42). */
export const KI_KNAPP_AB = KI_ROT_AB;

export interface KiSammelStand {
  gesamt: number;
  frei: number;
  /** Zugaenge ohne gueltige Messung — bewusst NICHT als frei und nicht als 0 % gezaehlt. */
  unbekannt: number;
  /** Hoechster gemessener Anteil „Kontingent genutzt" ueber alle Zugaenge; null = nichts gemessen. */
  hoechste: number | null;
  hoechsterZugang: string | null;
  /** Ein Zugang ist knapp (>= KI_KNAPP_AB), gesperrt oder widerspruechlich konfiguriert. */
  rot: boolean;
}

/**
 * Verdichtet die Messbalken zu EINER Aussage fuer die Kopfleiste. Fehlende oder
 * veraltete Messwerte bleiben unbekannt — eine 0 saehe aus wie „frei" (siehe UsagePill).
 */
export function kiSammelStand(accounts: UsageAccount[], health: Record<string, AccountHealth> = {}): KiSammelStand {
  let frei = 0, unbekannt = 0, hoechste: number | null = null, hoechsterZugang: string | null = null, rot = false;
  for (const a of accounts) {
    let werte: number[];
    let ungemessen: boolean;
    if (a.provider === 'codex') {
      werte = (a.quotaWindows ?? []).map(w => w.usedPercent).filter(v => typeof v === 'number');
      ungemessen = werte.length === 0 || !!a.quotaError;
    } else {
      const w = a.scraped?.weeklyAllModels.percent ?? null;
      const k = a.scraped?.currentSession.percent ?? null;
      werte = [w, k].filter((v): v is number => typeof v === 'number');
      ungemessen = w === null || k === null || a.status === 'stale';
    }
    const gesperrt = !!a.cliSperre || health[a.accountId]?.status === 'drift';
    const spitze = werte.length ? Math.max(...werte) : null;
    if (spitze !== null && (hoechste === null || spitze > hoechste)) { hoechste = spitze; hoechsterZugang = a.accountId; }
    if (gesperrt || (spitze !== null && spitze >= KI_KNAPP_AB)) rot = true;
    if (ungemessen) unbekannt += 1;
    else if (!gesperrt && spitze !== null && spitze < KI_KNAPP_AB) frei += 1;
  }
  return { gesamt: accounts.length, frei, unbekannt, hoechste, hoechsterZugang, rot };
}

